import type { ReactElement, Ref, SelectHTMLAttributes } from 'react'
import { useId } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  hint?: string
  error?: string
  options: Array<SelectOption>
  ref?: Ref<HTMLSelectElement>
}

export function Select({
  label,
  hint,
  error,
  options,
  className,
  id,
  children: _children,
  'aria-describedby': describedBy,
  ...rest
}: SelectProps): ReactElement {
  const generatedId = useId()
  const selectId = id ?? `select-${generatedId}`
  const hintId = `${selectId}-hint`
  const errorId = `${selectId}-error`
  const invalid = Boolean(error)
  const showHint = Boolean(hint) && !invalid
  const description =
    [describedBy, showHint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </label>
      ) : null}
      <div className="relative">
        <select
          id={selectId}
          aria-invalid={invalid || undefined}
          aria-describedby={description}
          className={cn(
            'block h-10 w-full cursor-pointer appearance-none rounded-control border bg-surface pl-3 pr-9 text-sm text-fg shadow-sheet transition-colors dark:bg-sunken',
            'focus:outline-none focus:ring-2',
            'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-subtle disabled:opacity-70',
            invalid
              ? 'border-bad-500 focus:border-bad-500 focus:ring-bad-500/30'
              : 'border-line hover:border-line-strong focus:border-accent focus:ring-accent/30',
            className
          )}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle"
          aria-hidden="true"
        />
      </div>
      {showHint ? (
        <p id={hintId} className="mt-1.5 text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
      {invalid ? (
        <p id={errorId} className="mt-1.5 text-xs font-medium text-bad-600 dark:text-bad-300">
          {error}
        </p>
      ) : null}
    </div>
  )
}
